import { inject, Injectable, signal } from '@angular/core';
import { User } from '../../api/app-types/user.app';
import { UserApi } from '../../api/services/user.service';

/**
 * Caches the authenticated `users/me` user for the whole app so `userResolver`, `Desk`, and
 * `Sidebar` share exactly one request instead of each fetching it independently.
 *
 * Deliberately root-scoped and NOT re-provided anywhere: any route/component-level provider for
 * this class would shadow this singleton and reintroduce duplicate requests.
 *
 * This only ever holds the AUTHENTICATED user (`UserApi.getSingleMe()` always sets
 * `skipActAs: true`, so its result never depends on - and is never affected by - representation
 * mode). Represented-user data is a separate concern and is intentionally not handled here.
 */
@Injectable({
	providedIn: 'root',
})
export class CurrentUserService {
	private readonly userApi = inject(UserApi);
	private readonly _currentUser = signal<User | null>(null);
	public readonly currentUser = this._currentUser.asReadonly();
	private inFlight: Promise<User> | undefined;

	public getCurrentUser(): Promise<User> {
		const cached = this._currentUser();
		if (cached) {
			return Promise.resolve(cached);
		}

		if (this.inFlight) {
			return this.inFlight;
		}

		this.inFlight = this.userApi
			.getSingleMe()
			.then((user) => {
				this._currentUser.set(user);
				return user;
			})
			.finally(() => {
				this.inFlight = undefined;
			});

		return this.inFlight;
	}

	/**
	 * Clears the cached user (and any in-flight request), forcing the next `getCurrentUser()`
	 * call to reload from the server. Should be called on logout/login-account-change.
	 */
	public clear(): void {
		this._currentUser.set(null);
		this.inFlight = undefined;
	}

	public async updateCurrentUser(): Promise<void> {
		const user = await this.userApi.getSingleMe();
		this._currentUser.set(user);
	}
}
